import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  BarChart3, Users, Clock, FileText, TrendingUp, Calendar,
  DollarSign, Receipt
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [userTimeReport, setUserTimeReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    fetchStats();
    fetchUserTimeReport();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reports/statistics`);
      setStats(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente statistik');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTimeReport = async (start, end) => {
    try {
      let url = `${API}/reports/user-time`;
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setUserTimeReport(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente tidsrapport');
    }
  };

  const handleDateFilter = () => {
    fetchUserTimeReport(dateRange.start, dateRange.end);
  };

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}t ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_workcards || 0}</div>
                <p className="text-sm text-muted-foreground">Arbejdskort i alt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Users className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_customers || 0}</div>
                <p className="text-sm text-muted-foreground">Kunder</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Clock className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_work_hours || 0}t</div>
                <p className="text-sm text-muted-foreground">Arbejdstimer i alt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Receipt className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_invoices || 0}</div>
                <p className="text-sm text-muted-foreground">Fakturaer</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <TrendingUp className="h-5 w-5 text-cyan-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.workcards_this_week || 0}</div>
                <p className="text-sm text-muted-foreground">Arbejdskort denne uge</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-500/10">
                <Calendar className="h-5 w-5 text-pink-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.workcards_this_month || 0}</div>
                <p className="text-sm text-muted-foreground">Arbejdskort denne måned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{(stats?.total_invoice_amount || 0).toLocaleString('da-DK')} kr</div>
                <p className="text-sm text-muted-foreground">Faktureret i alt</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Time Report */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Tidsrapport per Medarbejder
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Date Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Label>Fra dato</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                data-testid="report-start-date"
              />
            </div>
            <div className="flex-1">
              <Label>Til dato</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                data-testid="report-end-date"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleDateFilter} data-testid="filter-report-btn">
                Filtrer
              </Button>
            </div>
          </div>

          {/* User Stats from General Statistics */}
          {stats?.user_time?.length > 0 ? (
            <div className="space-y-4">
              {stats.user_time.map((userStat, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {userStat.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{userStat.username}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatMinutes(userStat.minutes)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{userStat.hours}t</div>
                    <p className="text-sm text-muted-foreground">Total arbejdstid</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Ingen tidsregistreringer fundet</p>
            </div>
          )}

          {/* Detailed User Time Report */}
          {userTimeReport.length > 0 && (
            <div className="mt-8 space-y-6">
              <h3 className="font-semibold text-lg">Detaljeret Oversigt</h3>
              {userTimeReport.map((user, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-lg">{user.username}</h4>
                    <div className="text-right">
                      <span className="text-xl font-bold">{user.total_hours}t</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({user.workcard_count} opgaver)
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {user.entries.slice(0, 10).map((entry, entryIdx) => (
                      <div key={entryIdx} className="flex justify-between text-sm py-1 border-b border-dashed last:border-0">
                        <span>
                          <span className="font-mono text-muted-foreground">#{entry.workcard_number}</span>
                          {' '}{entry.customer_name}
                        </span>
                        <span className="text-muted-foreground">
                          {entry.date} - {entry.duration_minutes} min
                        </span>
                      </div>
                    ))}
                    {user.entries.length > 10 && (
                      <p className="text-sm text-muted-foreground text-center pt-2">
                        + {user.entries.length - 10} flere poster
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Faktura Oversigt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats?.pending_invoices || 0}
              </div>
              <p className="text-sm text-muted-foreground">Afventende fakturaer</p>
            </div>
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats?.paid_invoices || 0}
              </div>
              <p className="text-sm text-muted-foreground">Betalte fakturaer</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {(stats?.total_invoice_amount || 0).toLocaleString('da-DK')} kr
              </div>
              <p className="text-sm text-muted-foreground">Total faktureret</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
